// Customer page section (office and admin, Session AUF, Director decisions DL-1 to DL-3): who arranges this customer's
// LOLER thorough examinations, and the re-bill of an examining body's invoice.
//
// THE DEFAULT IS THE CUSTOMER'S OWN ARRANGEMENT (DL-1). Recording that WE arrange them is an exception, set by a person with
// a reason (the quote or the email it rests on), previewed, then saved through a two-step confirm; audited server side. It
// changes the LOLER reminder's wording, the RFID page's examination wording, whether a reply may refer the sender to the
// recommended examining body, and whether "Re-bill to customer" is offered here.
//
// THE RE-BILL (DL-3), offered only while we arrange their examinations: the examining body's invoice (ex VAT) becomes ONE
// Xero DRAFT line, "LOLER thorough examination: <site>", at cost plus 35 percent. The office sees the cost and the result
// here; the customer's invoice shows the line and the price only. The draft is never authorised or sent.
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { LolerArrangement, LolerRebillResponse } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Loader } from "../../components/Loader";
import { buttonClasses } from "../../components/Button";

const FIELD_INPUT =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field-inset px-3 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green";
const FIELD_LABEL = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const LINK_BTN = "font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong disabled:opacity-50";
const BTN = "rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-strong transition hover:text-ele-green disabled:opacity-50";

const gbp = (n: number) => `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * rebillOnly (the Mail workspace, a supplier invoice email): the re-bill form alone, open, carrying the email it answers.
 */
export function CustomerLolerArrangementSection({ customerId, inboundEmailId, rebillOnly = false }: { customerId: string; inboundEmailId?: string; rebillOnly?: boolean }) {
  const [view, setView] = useState<LolerArrangement | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState<{ arranged: boolean; reason: string; after: LolerArrangement } | null>(null);
  const [busy, setBusy] = useState<"preview" | "save" | "rebill-preview" | "rebill" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [rebillOpen, setRebillOpen] = useState(rebillOnly);
  const [cost, setCost] = useState("");
  const [siteId, setSiteId] = useState("");
  const [supplierRef, setSupplierRef] = useState("");
  const [rebillPreview, setRebillPreview] = useState<LolerRebillResponse | null>(null);

  useEffect(() => {
    let live = true;
    setState("loading");
    Promise.resolve()
      .then(() => api.customers.lolerArrangement(customerId))
      .then((v) => {
        if (!live) return;
        setView(v);
        setSiteId(v.sites.length === 1 ? v.sites[0]!.id : "");
        setState("ready");
      })
      .catch(() => live && setState("error"));
    return () => {
      live = false;
    };
  }, [customerId]);

  async function preview(arranged: boolean) {
    setBusy("preview");
    setError(null);
    setMessage(null);
    try {
      const res = await api.customers.setLolerArrangement(customerId, { arranged, reason, preview: true });
      if (res.preview) setPending({ arranged, reason, after: res.after });
    } catch (e) {
      setError(friendlyApiError(e, "Could not check that change. Please try again in a moment."));
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    if (!pending) return;
    setBusy("save");
    setError(null);
    try {
      const res = await api.customers.setLolerArrangement(customerId, { arranged: pending.arranged, reason: pending.reason, preview: false });
      if (!res.preview) {
        setView(res);
        setMessage(res.arranged ? "Recorded: we arrange this customer's examinations." : "Recorded: the customer arranges their own examinations.");
        setPending(null);
        setEditing(false);
        setReason("");
      }
    } catch (e) {
      setError(friendlyApiError(e, "Could not save that change. Please try again in a moment."));
    } finally {
      setBusy(null);
    }
  }

  function rebillBody(previewOnly: boolean, again = false) {
    const n = Number(cost);
    return {
      costExVat: n,
      ...(siteId ? { siteId } : {}),
      ...(supplierRef.trim() ? { supplierInvoiceRef: supplierRef.trim() } : {}),
      ...(inboundEmailId ? { inboundEmailId } : {}),
      ...(again ? { again: true } : {}),
      preview: previewOnly,
    };
  }

  async function previewRebill() {
    setBusy("rebill-preview");
    setError(null);
    setMessage(null);
    setRebillPreview(null);
    try {
      setRebillPreview(await api.customers.lolerRebill(customerId, rebillBody(true)));
    } catch (e) {
      setError(friendlyApiError(e, "Could not work out the re-bill. Please try again in a moment."));
    } finally {
      setBusy(null);
    }
  }

  async function raiseRebill() {
    if (!rebillPreview) return;
    setBusy("rebill");
    setError(null);
    try {
      const res = await api.customers.lolerRebill(customerId, rebillBody(false, rebillPreview.alreadyRebilled != null));
      setMessage(`Xero draft raised${res.invoiceNumber ? ` (${res.invoiceNumber})` : ""} for ${gbp(res.amountExVat)} plus VAT. Review and authorise it in Xero.`);
      setRebillPreview(null);
      setRebillOpen(false);
      setCost("");
      setSupplierRef("");
    } catch (e) {
      setError(friendlyApiError(e, "Could not raise the Xero draft. Please try again in a moment."));
    } finally {
      setBusy(null);
    }
  }

  const costOk = Number.isFinite(Number(cost)) && Number(cost) > 0;

  return (
    <section className={rebillOnly ? "" : "rounded-ele border border-ele-purple-accent bg-ele-surface p-5"} aria-label="LOLER examinations">
      {!rebillOnly && <h2 className="mb-2 font-heading text-lg text-ele-blue">LOLER examinations</h2>}
      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" || !view ? (
        <p role="alert" className="text-sm text-ele-strong">
          Could not load who arranges this customer's examinations.
        </p>
      ) : (
        <div className="space-y-3">
          {!rebillOnly && (
          <>
          <p className="text-sm text-ele-strong" data-testid="loler-arrangement-status">
            {view.arranged ? "We arrange this customer's examinations." : "The customer arranges their own examinations (the default)."}
          </p>
          <p className="text-xs text-ele-body">{view.meaning}</p>
          {view.reason && (
            <p className="text-xs text-ele-body">
              Reason: {view.reason}
              {view.setByName ? ` (recorded by ${view.setByName})` : ""}
            </p>
          )}

          {!editing ? (
            <button type="button" className={LINK_BTN} onClick={() => setEditing(true)}>
              {view.arranged ? "The customer arranges their own" : "We arrange their examinations"}
            </button>
          ) : (
            <div className="space-y-2">
              <label className="block">
                <span className={FIELD_LABEL}>Reason (the quote, email or instruction it rests on)</span>
                <input
                  aria-label="Reason"
                  value={reason}
                  maxLength={500}
                  onChange={(e) => {
                    setReason(e.target.value);
                    setPending(null);
                  }}
                  className={FIELD_INPUT}
                />
              </label>
              <div className="flex flex-wrap items-center gap-3">
                <button type="button" className={BTN} disabled={busy != null || reason.trim().length < 3} onClick={() => void preview(!view.arranged)}>
                  {busy === "preview" ? "Checking..." : "Preview"}
                </button>
                <button
                  type="button"
                  className={LINK_BTN}
                  onClick={() => {
                    setEditing(false);
                    setPending(null);
                  }}
                >
                  Cancel
                </button>
              </div>
              {pending && (
                <div className="rounded-ele border border-ele-hairline p-3" data-testid="loler-arrangement-preview">
                  <p className="mb-2 text-sm text-ele-strong">{pending.after.meaning}</p>
                  <ConfirmButton
                    onConfirm={() => void save()}
                    label={pending.arranged ? "Record that we arrange them" : "Record that they arrange their own"}
                    confirmLabel="Their reminders and replies will follow it. Press again"
                    busy={busy === "save"}
                    busyLabel="Saving..."
                    disabled={busy != null}
                    className={buttonClasses("secondary", "sm")}
                    armedClassName="!text-ele-green font-semibold"
                  />
                </div>
              )}
            </div>
          )}

          </>
          )}

          {view.rebillOffered && (
            <div className={rebillOnly ? "" : "border-t border-ele-hairline pt-3"}>
              {!rebillOpen ? (
                <button type="button" className={LINK_BTN} onClick={() => setRebillOpen(true)}>
                  Re-bill to customer
                </button>
              ) : (
                <div className="space-y-2" data-testid="loler-rebill">
                  <p className="text-xs text-ele-body">
                    The examining body's invoice to us, re-billed to this customer as one Xero draft line. The customer sees the line and the price
                    only.
                  </p>
                  <div className="grid gap-2 sm:grid-cols-3">
                    <label className="block">
                      <span className={FIELD_LABEL}>Their invoice, ex VAT (£)</span>
                      <input
                        aria-label="Examining body's invoice ex VAT"
                        inputMode="decimal"
                        value={cost}
                        onChange={(e) => {
                          setCost(e.target.value);
                          setRebillPreview(null);
                        }}
                        className={FIELD_INPUT}
                      />
                    </label>
                    <label className="block">
                      <span className={FIELD_LABEL}>Site</span>
                      <select
                        aria-label="Site"
                        value={siteId}
                        onChange={(e) => {
                          setSiteId(e.target.value);
                          setRebillPreview(null);
                        }}
                        className={FIELD_INPUT}
                      >
                        <option value="">Choose a site</option>
                        {view.sites.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className={FIELD_LABEL}>Their invoice number (internal)</span>
                      <input
                        aria-label="Examining body's invoice number"
                        value={supplierRef}
                        maxLength={100}
                        onChange={(e) => {
                          setSupplierRef(e.target.value);
                          setRebillPreview(null);
                        }}
                        className={FIELD_INPUT}
                      />
                    </label>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <button type="button" className={BTN} disabled={busy != null || !costOk || !siteId} onClick={() => void previewRebill()}>
                      {busy === "rebill-preview" ? "Working it out..." : "Preview"}
                    </button>
                    <button
                      type="button"
                      className={LINK_BTN}
                      onClick={() => {
                        setRebillOpen(false);
                        setRebillPreview(null);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                  {rebillPreview && (
                    <div className="rounded-ele border border-ele-hairline p-3 text-sm" data-testid="loler-rebill-preview">
                      <p className="text-ele-strong">
                        The customer's invoice: "{rebillPreview.invoiceLine}", {gbp(rebillPreview.amountExVat)} plus VAT.
                      </p>
                      <p className="mt-1 text-xs text-ele-body">
                        For the office only: their invoice {gbp(rebillPreview.office.examiningBodyInvoiceExVat)}, re-billed at cost plus 35 percent.
                      </p>
                      {rebillPreview.alreadyRebilled && (
                        <p role="alert" className="mt-1 text-xs text-ele-danger">
                          This invoice was already re-billed on {rebillPreview.alreadyRebilled.at.slice(0, 10)}. Raise it again only if that draft was
                          voided.
                        </p>
                      )}
                      <div className="mt-2">
                        <ConfirmButton
                          onConfirm={() => void raiseRebill()}
                          label={rebillPreview.alreadyRebilled ? "Raise it again as a Xero draft" : "Raise the Xero draft"}
                          confirmLabel="A draft only: nothing is sent. Press again"
                          busy={busy === "rebill"}
                          busyLabel="Raising..."
                          disabled={busy != null}
                          className={buttonClasses("secondary", "sm")}
                          armedClassName="!text-ele-green font-semibold"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {error && (
            <p role="alert" className="text-sm text-ele-danger">
              {error}
            </p>
          )}
          {message && (
            <p role="status" className="text-sm text-ele-strong">
              {message}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
